import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { toast } from 'react-toastify';
import { Card, Input, Button, Space, Typography, Alert, Modal } from 'antd';
import {
    SearchOutlined,
    SafetyCertificateOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import { reactLocalStorage } from 'reactjs-localstorage';

import {
    APP_INFO,
    EMPTY,
    POST_DMT_CHECK_SERVICE_TO_MOBILENUMBER,
    POST_DMT_REFRESH_SERVICE,
    GET_MYPROFILE,
    FINO_IMW_CHECK_SENDER,
    AIRTEL_IMW_CHECK_SENDER,
    DMT_SETTING_URL,
} from './../../Utils/appConstants';
import { isNumber } from '../../Utils/Common';
import { postRequest, getRequest } from '../../Utils/AppApi';

import InstantPay from '../partnerEKYC/instantPay';
import MoneyTransfer from '../moneyTransfer/landing';
import MoneyTransferV2 from '../airteldmtImw/index';
import MoneyTransferV3 from '../moneyTransferV3/index';
import MoneyTransferV2_1 from '../moneyTransferV2_1/index';
import MoneyTransferV2_2 from '../moneyTransferV2_2/index';
import MoneyTransferFinoDMT from '../finodmtImw/index';

// ---- Operator logos ----
import airtelLogo from './../../assets/airtel_icon.png';
import nsdlLogo from './../../assets/nsdl_icon.png';
import jioLogo from './../../assets/jio_icon.png';
import finoLogo from './../../assets/fino_logo.png';

const { Text } = Typography;

// ✅ Fixed bank display names — same as Android dialog
const SERVICE_NAMES = {
    DMT: 'Fino DMT',
    DMT31: 'Airtel DMT',
    DMT11: 'NSDL DMT',
    DMT2: 'Fino DMT',
    DMT21: 'JIO DMT',
    DMT3: 'Fino DMT',
};

// Map each service code to its operator logo
const SERVICE_LOGOS = {
    DMT: finoLogo,
    DMT31: airtelLogo,
    DMT11: nsdlLogo,
    DMT2: finoLogo,
    DMT21: jioLogo,
    DMT3: finoLogo,
};

// ---- Codes whose referenceKey comes from POST_DMT_REFRESH_SERVICE ----
const LEGACY_CODES_WITH_REF_KEY = ['DMT31', 'DMT11', 'DMT21'];

// ---- Codes that use their dedicated IMW sender-check endpoint ----
const IMW_CODES = {
    DMT3: 'fino',   // uses FINO_IMW_CHECK_SENDER
    DMT31: 'airtel', // uses AIRTEL_IMW_CHECK_SENDER
};

// ---- Validation helpers ----
const isRepeatedDigits = (mobile) => /^(\d)\1{9}$/.test(mobile);
const isSequentialDigits = (mobile) => {
    const digits = mobile.split('').map(Number);
    const ascending = digits.every((d, i) => i === 0 || d === digits[i - 1] + 1);
    const descending = digits.every((d, i) => i === 0 || d === digits[i - 1] - 1);
    return ascending || descending;
};
const isValidMobile = (mobile) => {
    if (!/^[6-9]\d{9}$/.test(mobile))
        return { valid: false, message: 'Invalid mobile number. Must be a 10-digit Indian number starting with 6, 7, 8, or 9.' };
    if (isRepeatedDigits(mobile))
        return { valid: false, message: 'Invalid mobile number. Repeated digits (e.g., 9999999999) are not allowed.' };
    if (isSequentialDigits(mobile))
        return { valid: false, message: 'Invalid mobile number. Sequential digits (e.g., 1234567890) are not allowed.' };
    return { valid: true, message: '' };
};

// Some backend responses still use the legacy "DMT1" code for
// Airtel DMT. The rest of the app (names, logos, IMW routing,
// switch cases, settings map) keys on "DMT31".
const normalizeServiceCode = (code) => (code === 'DMT1' ? 'DMT31' : code);

const IndexComponent = () => {
    const history = useHistory();
    const [isLoading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isCustomerService, setCustomerService] = useState(null);
    const [appData, setAppData] = useState(null);
    const [isSrpayAvailableLimit, setSrpayAvailableLimit] = useState(null);
    const [userProfile, setUserProfile] = useState(null);

    const [serviceModalVisible, setServiceModalVisible] = useState(false);
    const [tempSelectedService, setTempSelectedService] = useState(null);

    const [isDmt, setDmt] = useState(false);
    const [isDmt1, setDmt1] = useState(false);
    const [isDmt11, setDmt11] = useState(false);
    const [isDmt2, setDmt2] = useState(false);
    const [isDmt21, setDmt21] = useState(false);
    const [isDmt3, setDmt3] = useState(false);

    const [refreshingService, setRefreshingService] = useState(null);

    // ---- referenceKey + validity from POST_DMT_REFRESH_SERVICE ----
    const [refreshedRefKey, setRefreshedRefKey] = useState(null);
    const [refreshedValidity, setRefreshedValidity] = useState(null);

    // ---- DMT settings from backend { [code]: { flag, message, ... } } ----
    const [dmtSettings, setDmtSettings] = useState({});
    const [dmtSettingsLoading, setDmtSettingsLoading] = useState(true);

    const inputRef = useRef(null);
    const appLocalData = reactLocalStorage.get(APP_INFO, EMPTY, true);

    useEffect(() => { inputRef.current?.focus(); }, []);

    useEffect(() => {
        if (appLocalData) {
            try { setAppData(JSON.parse(appLocalData)); }
            catch (e) { console.error('Failed to parse appLocalData:', e); }
        }
    }, [appLocalData]);

    useEffect(() => { getProfile(); }, []); // eslint-disable-line

    // ---- load DMT settings once ----
    useEffect(() => {
        const fetchDmtSettings = async () => {
            try {
                const response = await getRequest(DMT_SETTING_URL, history);
                if (response?.success && Array.isArray(response.data)) {
                    const map = {};
                    response.data.forEach((item) => {
                        if (item?.code) {
                            // Normalize legacy DMT1 -> DMT31 so lookups match
                            map[normalizeServiceCode(item.code)] = item;
                        }
                    });

                    setDmtSettings(map);
                }
            } catch (err) {
                console.error('Failed to load DMT settings:', err);
            } finally {
                setDmtSettingsLoading(false);
            }
        };
        fetchDmtSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- is a service code disabled by DMT settings? ----
    const getServiceAvailability = (code) => {
        const setting = code ? dmtSettings[code] : null;
        if (!setting) return { disabled: false, message: '' };
        const disabled = setting.flag === false; // flag:false => disabled
        return {
            disabled,
            message: disabled
                ? (setting.message || 'This service is currently under maintenance.')
                : '',
        };
    };

    const getProfile = async () => {
        try {
            const response = await getRequest(GET_MYPROFILE, history);
            if (response.success) setUserProfile(response.data[0]);
        } catch (error) {
            console.error('Error fetching profile:', error);
        }
    };

    const resetServiceFlags = () => {
        setDmt(false);
        setDmt1(false);
        setDmt11(false);
        setDmt2(false);
        setDmt21(false);
        setDmt3(false);
    };

    const clearRefreshedRefKey = () => {
        setRefreshedRefKey(null);
        setRefreshedValidity(null);
    };

    const handleSearch = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setSearchText(value);
        setErrorMessage('');
        setCustomerService(null);
        resetServiceFlags();
        clearRefreshedRefKey();
    };

    const clearInput = () => {
        setSearchText('');
        setErrorMessage('');
        setCustomerService(null);
        resetServiceFlags();
        clearRefreshedRefKey();
        inputRef.current?.focus();
    };

    const checkServices = async (mobile) => {
        const validation = isValidMobile(mobile);
        if (!validation.valid) {
            setErrorMessage(validation.message);
            return;
        }

        setLoading(true);
        setErrorMessage('');
        resetServiceFlags();
        setCustomerService(null);
        clearRefreshedRefKey();

        const payload = { customerMobile: mobile };
        try {
            const response = await postRequest(POST_DMT_CHECK_SERVICE_TO_MOBILENUMBER, payload, history);
            if (response.success) {
                setSrpayAvailableLimit(response.srpayPayAvailableLimit);

                // Map legacy DMT1 -> DMT31 so downstream logic (logos, names,
                // availability lookup, IMW routing, submit switch) all match.
                const services = (response.data || []).map((s) => ({
                    ...s,
                    code: normalizeServiceCode(s.code),
                }));

                setCustomerService(services);
                setTempSelectedService(null);
                setServiceModalVisible(true);
            } else {
                if (response.rekyc) { history.push('/partner-ekyc'); return; }
                toast.error(response.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('API Error:', error);
            toast.error('Failed to check service');
        } finally {
            setLoading(false);
        }
    };

    // ---- Extract referenceKey / validity from the refresh response ----
    const pickRefKeyFromRefresh = (response) => {
        const cd = response?.data?.customerData;
        if (!cd) return { rk: null, validity: null };
        return {
            rk: cd.referenceKey || null,
            validity: cd.validity || null,
        };
    };

    // ---- Shared helper: handle the Re-KYC redirect pattern ----
    const isRekycResponse = (response) =>
        response?.rekyc === true || response?.rekyc === 'true' || response?.rekyc === 1;

    const refreshAndProceed = async (record) => {
        // Double-check the service is enabled before hitting the API
        const avail = getServiceAvailability(record.code);
        if (avail.disabled) {
            toast.error(avail.message || 'This service is under maintenance.');
            return;
        }

        setRefreshingService(record.code);
        clearRefreshedRefKey();

        // ============================================================
        // IMW branch: Fino (DMT3) → FINO_IMW_CHECK_SENDER
        //             Airtel (DMT31) → AIRTEL_IMW_CHECK_SENDER
        // ============================================================
        const imwType = IMW_CODES[record.code];
        if (imwType) {
            const endpoint =
                imwType === 'airtel' ? AIRTEL_IMW_CHECK_SENDER : FINO_IMW_CHECK_SENDER;

            const opLabel = imwType === 'airtel' ? 'Airtel' : 'Fino';

            try {
                const payload = { senderMobile: searchText };
                const response = await postRequest(endpoint, payload, history);

                if (response.success || (response.data && Object.keys(response.data).length > 0)) {
                    handleSubmit(record);
                } else {
                    if (isRekycResponse(response)) {
                        history.push('/partner-ekyc');
                        return;
                    }
                    toast.error(response.message || `Failed to check ${opLabel} sender`);
                }
            } catch (error) {
                console.error(`${opLabel} IMW refresh error:`, error);
                toast.error(`Could not check ${opLabel} sender`);
            } finally {
                setRefreshingService(null);
            }
            return;
        }

        // ============================================================
        // Generic branch: InstantPay / NSDL / JIO / V2 base
        // ============================================================
        try {
            const payload = { customerMobile: searchText, serviceCode: record.code };
            const response = await postRequest(POST_DMT_REFRESH_SERVICE, payload, history);

            if (response.success || (response.data && Object.keys(response.data).length > 0)) {
                if (LEGACY_CODES_WITH_REF_KEY.includes(record.code)) {
                    const { rk, validity } = pickRefKeyFromRefresh(response);
                    if (rk) {
                        setRefreshedRefKey(rk);
                        console.log(`[IndexComponent] saved referenceKey for ${record.code}:`, rk);
                    }
                    if (validity) {
                        setRefreshedValidity(validity);
                        console.log(`[IndexComponent] saved validity for ${record.code}:`, validity);
                    }
                }
                handleSubmit(record);
            } else {
                if (isRekycResponse(response)) {
                    history.push('/partner-ekyc');
                    return;
                }
                toast.error(response.message || 'Failed to refresh service');
            }
        } catch (error) {
            console.error('Refresh error:', error);
            toast.error('Could not refresh service');
        } finally {
            setRefreshingService(null);
        }
    };

    const handleSubmit = (record) => {
        setServiceModalVisible(false);
        setCustomerService(null);
        setTempSelectedService(null);

        switch (record.code) {
            case 'DMT31': setDmt1(true); break;
            case 'DMT2': setDmt2(true); break;
            case 'DMT11': setDmt11(true); break;
            case 'DMT21': setDmt21(true); break;
            case 'DMT3': setDmt3(true); break;
            case 'DMT': setDmt(true); break;
            default: break;
        }
    };

    const confirmService = () => {
        if (!tempSelectedService) return;
        const avail = getServiceAvailability(tempSelectedService.code);
        if (avail.disabled) {
            toast.error(avail.message || 'This service is under maintenance.');
            return;
        }
        refreshAndProceed(tempSelectedService);
    };

    const goBackToSearch = () => {
        resetServiceFlags();
        setSearchText('');
        setCustomerService(null);
        setErrorMessage('');
        setServiceModalVisible(false);
        setTempSelectedService(null);
        clearRefreshedRefKey();
        inputRef.current?.focus();
    };

    const isAnyServiceActive = isDmt || isDmt1 || isDmt11 || isDmt2 || isDmt21 || isDmt3;

    // ===================== MOBILE-STYLE SERVICE MODAL =====================
    const renderServiceModal = () => {
        const services = isCustomerService || [];

        return (
            <Modal
                open={serviceModalVisible}
                footer={null}
                closable={false}
                maskClosable={false}
                centered
                width={400}
                bodyStyle={{ padding: 0 }}
                modalRender={(node) => (
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    }}>
                        {node}
                    </div>
                )}
            >
                {/* ===== Header ===== */}
                <div style={{
                    padding: '18px 20px 12px',
                    textAlign: 'center',
                    borderBottom: '1px solid #f1f5f9',
                }}>
                    <div style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#5851D2',
                        letterSpacing: '-0.2px',
                    }}>
                        Select DMT Service
                    </div>
                </div>

                {/* ===== Optional Available Limit ===== */}
                {appData?.dmtTransactionLimit && isSrpayAvailableLimit != null && (
                    <div style={{
                        padding: '10px 20px 0',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        fontSize: '13px',
                    }}>
                        <span style={{ color: '#5851D2' }}>Available Limit:&nbsp;</span>
                        <span style={{ color: '#ef4444', fontWeight: 700 }}>{isSrpayAvailableLimit}</span>
                    </div>
                )}

                {/* ===== Service Rows ===== */}
                <div style={{
                    padding: '16px 16px 8px',
                    maxHeight: '55vh',
                    overflowY: 'auto',
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {services.map((service) => {
                            const availability = getServiceAvailability(service.code);
                            const isDisabled = availability.disabled;
                            const statusOk = service.serviceStatus === 'Available';
                            const isAvailable = statusOk && !isDisabled;
                            const isRegistered = service.customerStatus === 'Registered';
                            const isSelected = tempSelectedService?.code === service.code;
                            const logo = SERVICE_LOGOS[service.code];
                            const bankName = SERVICE_NAMES[service.code] || service.serviceName;

                            return (
                                <div
                                    key={service.code}
                                    onClick={() => isAvailable && setTempSelectedService(service)}
                                    style={{
                                        padding: '12px 14px',
                                        borderRadius: '12px',
                                        background: isSelected
                                            ? '#eef2ff'
                                            : (isAvailable ? '#f8fafc' : '#fef2f2'),
                                        border: `2px solid ${isSelected
                                                ? '#5851D2'
                                                : (isAvailable ? '#e2e8f0' : '#fecaca')
                                            }`,
                                        cursor: isAvailable ? 'pointer' : 'not-allowed',
                                        transition: 'all 0.15s ease',
                                        boxShadow: isSelected
                                            ? '0 0 0 4px rgba(88,81,210,0.15)'
                                            : 'none',
                                        opacity: isAvailable ? 1 : 0.6,
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}>
                                        {logo && (
                                            <img
                                                src={logo}
                                                alt={bankName}
                                                style={{
                                                    width: 32,
                                                    height: 32,
                                                    objectFit: 'contain',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <div style={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            color: '#000000',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>
                                            {bankName}
                                        </div>
                                        {isDisabled ? (
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                color: '#ef4444',
                                            }}>
                                                🔧 Maintenance
                                            </div>
                                        ) : (
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                color: isRegistered ? '#22c55e' : '#ef4444',
                                            }}>
                                                {isRegistered ? '✅ Registered' : '❌ Not Registered'}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{
                                        marginLeft: 44,
                                        marginTop: 4,
                                        fontSize: '13px',
                                        color: isDisabled ? '#ef4444' : '#5851D2',
                                        fontWeight: 500,
                                    }}>
                                        {isDisabled
                                            ? availability.message
                                            : `Limit: ${service.remainingLimit ?? 'N/A'}`}
                                    </div>

                                    {!isDisabled && !statusOk && (
                                        <div style={{
                                            marginLeft: 44,
                                            marginTop: 2,
                                            fontSize: '12px',
                                            color: '#ef4444',
                                            fontWeight: 600,
                                        }}>
                                            {service.serviceStatus}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ===== Hindi note ===== */}
                <div style={{
                    padding: '0 16px 8px',
                    textAlign: 'center',
                    fontSize: '11px',
                    color: '#94a3b8',
                    lineHeight: 1.4,
                }}>
                    एक महीने में आप सभी विकल्पों से केवल Rs. 25000 / Customer ही ट्रांसफर कर सकते हैं!
                </div>

                {/* ===== Buttons ===== */}
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    padding: '12px 16px 16px',
                }}>
                    <button
                        onClick={goBackToSearch}
                        style={{
                            flex: 1,
                            height: 44,
                            borderRadius: '10px',
                            border: 'none',
                            background: '#e2e8f0',
                            color: '#475569',
                            fontSize: '15px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: 0,
                            lineHeight: 1,
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#cbd5e1'}
                        onMouseOut={(e) => e.currentTarget.style.background = '#e2e8f0'}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={confirmService}
                        disabled={!tempSelectedService || refreshingService !== null || dmtSettingsLoading}
                        style={{
                            flex: 1,
                            height: 44,
                            borderRadius: '10px',
                            border: 'none',
                            background: tempSelectedService
                                ? 'linear-gradient(135deg, #5851D2 0%, #6d66e0 100%)'
                                : '#cbd5e1',
                            color: '#ffffff',
                            fontSize: '15px',
                            fontWeight: 700,
                            cursor: (tempSelectedService && refreshingService === null) ? 'pointer' : 'not-allowed',
                            opacity: refreshingService !== null ? 0.7 : 1,
                            transition: 'all 0.15s',
                            boxShadow: tempSelectedService
                                ? '0 4px 14px rgba(88,81,210,0.35)'
                                : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: 0,
                            lineHeight: 1,
                        }}
                    >
                        {refreshingService !== null ? 'Please wait…' : 'Continue'}
                    </button>
                </div>
            </Modal>
        );
    };

    if (isLoading) {
        return (
            <div className="content text-center" style={{ margin: '250px 0' }}>
                <PropagateLoader color="#5851D2" loading={isLoading} size={25} />
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(165deg, #f8f9fc 0%, #f1f0f9 45%, #f7f5ff 100%)',
            padding: '20px 10px 50px',
        }}>
            <div style={{ width: '100%' }}>
                {!isAnyServiceActive ? (
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: 18,
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04), 0 12px 28px -4px rgba(0,0,0,0.07)',
                            background: '#ffffff',
                            border: '1px solid rgba(0,0,0,0.03)',
                        }}
                        bodyStyle={{ padding: '22px 20px' }}
                    >
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                            <div>
                                <Text style={{
                                    fontSize: 14, color: '#3020be', fontWeight: 600,
                                    letterSpacing: '0.5px', textTransform: 'uppercase',
                                }}>
                                    Customer Mobile Number
                                </Text>
                                <Text style={{
                                    display: 'block', marginTop: 4, fontSize: 14,
                                    color: '#64748b', fontWeight: 400,
                                }}>
                                    Enter the 10‑digit mobile number to check available DMT services
                                </Text>
                            </div>

                            <div style={{
                                display: 'flex', flexWrap: 'wrap', gap: 12,
                                background: '#f8fafc', borderRadius: 14, padding: 8,
                                border: `1.5px solid ${errorMessage ? '#fca5a5' : '#e2e8f0'}`,
                                transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                                boxShadow: errorMessage
                                    ? '0 0 0 4px rgba(248,113,113,0.12)'
                                    : '0 2px 4px rgba(0,0,0,0.02)',
                            }}>
                                <Input
                                    ref={inputRef}
                                    size="large"
                                    placeholder="9876543210"
                                    value={searchText}
                                    onChange={handleSearch}
                                    onKeyDown={isNumber}
                                    onPressEnter={() => checkServices(searchText)}
                                    prefix={
                                        <SearchOutlined style={{ color: '#94a3b8', fontSize: 18, marginRight: 6 }} />
                                    }
                                    suffix={searchText ? (
                                        <CloseCircleOutlined
                                            onClick={clearInput}
                                            style={{ color: '#94a3b8', fontSize: 18, cursor: 'pointer' }}
                                        />
                                    ) : null}
                                    maxLength={10}
                                    bordered={false}
                                    disabled={isLoading}
                                    style={{
                                        flex: 1, minWidth: 180, background: 'transparent',
                                        fontSize: 18, fontWeight: 500, letterSpacing: '0.5px',
                                        color: '#1e293b', height: 48,
                                    }}
                                />
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => checkServices(searchText)}
                                    loading={isLoading}
                                    disabled={searchText.length !== 10}
                                    icon={<SafetyCertificateOutlined />}
                                    style={{
                                        borderRadius: 10, height: 48, padding: '0 28px',
                                        fontWeight: 600, fontSize: 15,
                                        background: searchText.length === 10
                                            ? 'linear-gradient(135deg, #5851D2 0%, #6d66e0 100%)'
                                            : undefined,
                                        border: 'none',
                                        boxShadow: searchText.length === 10
                                            ? '0 4px 14px rgba(88, 81, 210, 0.35)'
                                            : 'none',
                                        letterSpacing: '0.3px',
                                    }}
                                >
                                    Submit
                                </Button>
                            </div>

                            {errorMessage && (
                                <Alert
                                    message={errorMessage}
                                    type="error"
                                    showIcon
                                    closable
                                    onClose={() => setErrorMessage('')}
                                    style={{
                                        borderRadius: 10,
                                        border: '1px solid #fecaca',
                                        background: '#fef2f2',
                                    }}
                                />
                            )}

                            {appData?.dmtTransactionLimit && isSrpayAvailableLimit != null && (
                                <Text style={{ display: 'block', textAlign: 'right', fontSize: 13, color: '#64748b' }}>
                                    Available Limit Rs:{' '}
                                    <span style={{ color: '#ef4444', fontWeight: 700 }}>{isSrpayAvailableLimit}</span>
                                </Text>
                            )}
                        </Space>
                    </Card>
                ) : (
                    <div>
                        {/* <button
                            onClick={goBackToSearch}
                            style={{
                                background: 'transparent', border: 'none',
                                color: '#C9262C', fontSize: '16px', fontWeight: 600,
                                cursor: 'pointer', padding: '8px 12px', marginBottom: '10px',
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                borderRadius: '8px', transition: 'background 0.2s',
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <span style={{ fontSize: '20px' }}>←</span> Back
                        </button> */}

                        {isDmt && <MoneyTransfer mobile={searchText} />}
                        {isDmt1 && (
                            <>
                                <InstantPay />
                                <MoneyTransferV2
                                    mobile={searchText}
                                    referenceKey={refreshedRefKey}
                                    validity={refreshedValidity}
                                />
                            </>
                        )}
                        {isDmt11 && (
                            <MoneyTransferV2_1
                                mobile={searchText}
                                referenceKey={refreshedRefKey}
                                validity={refreshedValidity}
                            />
                        )}
                        {isDmt2 && <MoneyTransferV3 mobile={searchText} />}
                        {isDmt21 && (
                            <MoneyTransferV2_2
                                mobile={searchText}
                                referenceKey={refreshedRefKey}
                                validity={refreshedValidity}
                            />
                        )}
                        {isDmt3 && <MoneyTransferFinoDMT mobile={searchText} />}
                    </div>
                )}
            </div>

            {renderServiceModal()}

            <style>{`
                .ant-input:focus { box-shadow: none !important; }
            `}</style>
        </div>
    );
};

export default IndexComponent;